import { gql, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { SubStepTypes } from '@generated/graphql';
import { type CustomField, FETCH_CUSTOM_FIELDS } from '@interfaces/customField';
import type { ForeignTaxInformation } from '@interfaces/foreignTaxInformation';
import type { User } from '@interfaces/user';
import { CountryCodes } from '@onevesthq/ov-enums';
import { getWorkflowCompletionHeaders } from '@ovComponents/4-module/workflowCompletion/workflowCompletionHeaders';
import { UserContext } from '@providers/userContextProvider';
import { modifyUpdatingCustomFields } from '@utils/modifyUpdatingCustomFields';
import { useToast } from 'hephaestus/web';
import { isEmpty } from 'lodash-es';
import omitDeep from 'omit-deep-lodash';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermission } from '../../../../../hooks/usePermission';
import { getEnabledCustomFields } from '../utils';
import { ResidencyInformationVisual } from './residencyInformation.visual';

const FETCH_USER_FIELDS = (
  { canReadClientTaxidDisclosed }: { canReadClientTaxidDisclosed: boolean },
  derivedCustomFields?: string[],
  includeOrganization = true,
) => `#graphql
  user {
    id: stringId
    type
    citizenships
    foreignTaxExists
    foreignTaxInformation {
      id
      foreignTaxCountry
      foreignTaxNumber
      ${canReadClientTaxidDisclosed ? 'foreignTaxNumberDisclosed' : ''}
    }
    countryOfTaxResidence
    yearEnd
    taxNumber
    taxIdType
    taxId
    ${canReadClientTaxidDisclosed ? 'taxIdDisclosed' : ''}
    fatcaStatus
    fatcaStatusOtherDescription
    ${
      includeOrganization
        ? `organization {
      applicableLocalization {
        countries
      }
    }`
        : ''
    }
    ${
      derivedCustomFields
        ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
      key
      value
      customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format}
      selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
    }`
        : ''
    }
  }
`;

const FETCH_USER = (
  { canReadClientTaxidDisclosed }: { canReadClientTaxidDisclosed: boolean },
  derivedCustomFieldsKeys?: string[],
) => gql`
  query fetchUserResidencyInformation($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      ${FETCH_USER_FIELDS({ canReadClientTaxidDisclosed }, derivedCustomFieldsKeys)}
    }
  }
`;

const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
      }
    }
  }
`;

export const ResidencyInformation = ({
  options,
  userId,
  onNext,
  stepLoading,
  workflowCompletion,
  grid = false,
  updateMode = false,
  autoSelectCountryOfTaxResidence = true,
}: {
  options: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
  grid?: boolean;
  updateMode?: boolean;
  autoSelectCountryOfTaxResidence?: boolean;
}) => {
  const { t } = useTranslation(['shared']);
  const { enqueueToast } = useToast();
  const canReadCustomFields = usePermission('read:custom_fields');
  const canReadClientTaxidDisclosed = usePermission(
    'read:client_taxid_disclosed',
  );
  const [userData, setUserDataState] = useState<Partial<User>>({
    citizenships: [],
    sin: '',
    yearEnd: '',
    taxNumber: '',
    countryOfTaxResidence: undefined,
    foreignTaxInformation: [],
    customFields: [],
  });
  const setUserData: typeof setUserDataState = (payload) =>
    setUserDataState((previousData) => {
      const newData =
        typeof payload === 'function' ? payload(previousData) : payload;
      return {
        ...newData,
        foreignTaxExists: !!newData.foreignTaxInformation?.length,
      };
    });
  const [applicableCountries, setApplicableCountries] = useState<
    CountryCodes[] | undefined
  >();

  const [derivedCustomFields, setDerivedCustomFields] =
    useState<CustomField[]>();
  const [derivedCustomFieldsKeys, setDerivedCustomFieldsKeys] =
    useState<string[]>();
  const enabledCustomFields = getEnabledCustomFields(options);
  const { activeOrganization } = useContext(UserContext);

  const processUserResponse = (user: Partial<User>) => {
    setUserData({
      sin: undefined,
      type: user.type,
      taxId: user.taxId || undefined,
      taxIdDisclosed: user.taxIdDisclosed || undefined,
      taxIdType: user.taxIdType || undefined,
      yearEnd: user.yearEnd || undefined,
      taxNumber: user.taxNumber || undefined,
      countryOfTaxResidence: user.countryOfTaxResidence || undefined,
      citizenships: user.citizenships || [],
      foreignTaxInformation: omitDeep(
        user.foreignTaxInformation ?? [],
        '__typename',
      ) as ForeignTaxInformation[],
      foreignTaxExists: user.foreignTaxExists || undefined,
      fatcaStatus: user.fatcaStatus || undefined,
      fatcaStatusOtherDescription:
        user.fatcaStatusOtherDescription || undefined,
      customFields: user.customFields || [],
    });
    setApplicableCountries(
      user?.organization?.applicableLocalization?.countries ?? [
        CountryCodes.CA,
      ],
    );
  };

  const customFieldsPermitted =
    canReadCustomFields && enabledCustomFields.length !== 0;

  // loading 1 - custom fields
  const { loading: customFieldsLoading } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          workflowStep: SubStepTypes.ResidencyInformation,
          organizationId: activeOrganization?.id ?? undefined,
          keys: !isEmpty(enabledCustomFields) ? enabledCustomFields : undefined,
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !customFieldsPermitted,
    onCompleted: async (res: any) => {
      setDerivedCustomFields(res?.fetchCustomFields?.customFields);
      const customFieldKeys = res?.fetchCustomFields?.customFields?.map(
        (a: any) => a.key,
      );
      setDerivedCustomFieldsKeys(customFieldKeys);
    },
  });

  // loading 2 - user's data
  const { loading } = useQuery(
    FETCH_USER({ canReadClientTaxidDisclosed }, derivedCustomFieldsKeys),
    {
      variables: { userId },
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      skip: customFieldsLoading,
      onCompleted: (data) => {
        processUserResponse(data.fetchUser.user);
      },
    },
  );

  const [updateUserMutation, { loading: updating }] = useMutation(UPDATE_USER, {
    variables: {
      input: {
        ...omitDeep(userData, [
          'taxIdDisclosed',
          'foreignTaxNumberDisclosed',
          'foreignTaxExists',
        ]),
        ...(enabledCustomFields && derivedCustomFields && userData?.customFields
          ? {
              customFields: modifyUpdatingCustomFields(
                userData.customFields,
                derivedCustomFields,
                derivedCustomFieldsKeys,
              ),
            }
          : {}),
        userId,
      },
    },
    context: {
      headers: getWorkflowCompletionHeaders(workflowCompletion),
    },
  });
  const updateUser = async () => {
    await updateUserMutation();
    if (updateMode) {
      enqueueToast(t('profileDetailsUpdated'), { variant: 'success' });
    }
    await onNext();
  };

  return (
    <ResidencyInformationVisual
      options={options}
      userData={userData}
      updateUser={setUserData}
      continueFunc={updateUser}
      loading={loading || stepLoading || customFieldsLoading}
      updating={updating}
      applicableCountries={applicableCountries}
      grid={grid}
      updateMode={updateMode}
      workflowCompletion={workflowCompletion}
      activeCustomFields={derivedCustomFieldsKeys}
      derivedCustomFields={derivedCustomFields}
      autoSelectCountryOfTaxResidence={autoSelectCountryOfTaxResidence}
    />
  );
};

export default ResidencyInformation;
